import React, { useState } from "react";
import axios from "axios";
import { ShoppingBag, Minus, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../../config";
import { formatHUF, getDisplayPrice } from "../../lib/format";
import { useCart, useFanClub } from "../../contexts/AppContext";

export default function CartDrawer() {
  const { cartItems, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, clearCart } = useCart();
  const { member, tiers } = useFanClub();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const cartDisplayTotal = cartItems.reduce((sum, item) => sum + getDisplayPrice(item.price, member, tiers) * item.quantity, 0);

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;
    setIsCheckingOut(true);
    try {
      const payload = { items: cartItems, origin_url: window.location.origin };
      if (member?.id) payload.fan_club_member_id = member.id;
      const response = await axios.post(`${API}/checkout/create-session`, payload);
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      const msg = error.response?.data?.error || 'Hiba történt a fizetés indításakor';
      toast.error(msg);
    }
    finally { setIsCheckingOut(false); }
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-md bg-card border-l border-white/10">
        <SheetHeader><SheetTitle className="font-['Syne'] text-xl">Kosár</SheetTitle></SheetHeader>
        <div className="flex flex-col h-full mt-6">
          {cartItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">A kosarad üres</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {cartItems.map((item, index) => (
                  <div key={`${item.product_id}-${item.size ?? ""}-${item.color ?? ""}-${index}`} className="flex gap-4 p-3 bg-muted/50 rounded-lg">
                    <img src={item.image?.startsWith?.("http") ? item.image : `${BACKEND_URL}/storage/${item.image}`} alt={item.product_name} className="w-20 h-20 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.product_name}</p>
                      {(item.size || item.color) && (
                        <p className="text-xs text-muted-foreground">
                          {[item.size, item.color].filter(Boolean).join(" • ")}
                        </p>
                      )}
                      <p className="text-primary font-semibold mt-1">{formatHUF(getDisplayPrice(item.price, member, tiers))}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity - 1)}><Minus className="h-3 w-3" /></Button>
                        <span className="text-sm w-6 text-center">{item.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity + 1)}><Plus className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-destructive" onClick={() => removeFromCart(item.product_id, item.size, item.color)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-white/10 mt-4 space-y-4 pb-20">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Összesen</span>
                  <span className="text-2xl font-bold text-primary">{formatHUF(cartDisplayTotal)}</span>
                </div>
                <Button className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-bold text-lg" onClick={handleCheckout} disabled={isCheckingOut}>
                  {isCheckingOut ? 'Feldolgozás...' : 'Fizetés'}
                </Button>
                <Button variant="ghost" className="w-full" onClick={clearCart}>Kosár ürítése</Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
